import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { UseQueryResult, useQueryClient } from '@tanstack/react-query';

import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import { getBasePath } from './getAISBasePath';
import {
  Configuration,
  CoverageApi,
  CoverageImagePost200Response,
  type SourceIdentifier,
} from '@ais-ui/api-spec/coverage';
import { DateRange } from 'react-day-picker';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

export const useCoverageApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/covserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/covserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const coverageApi = useMemo(
    () => new CoverageApi(configuration),
    [configuration],
  );
  return apiClientWrapper(coverageApi) as CoverageApi;
};

export const useGetSourceIdentifiers = (
  enabled: boolean,
): UseQueryResult<Array<SourceIdentifier>> => {
  const coverageApi = useCoverageApi();

  return useQuery(
    {
      queryKey: ['coverageSourceIdentifiers'],
      queryFn: async () => {
        return await coverageApi.sourceIdentifiersGet({ covEnabled: true });
      },
      enabled: enabled,
    },
    'coverage:getSourceIdentifiers',
  );
};

const formatDateRange = (range: DateRange): string => {
  const format = (date: Date | undefined) =>
    date ? date.toISOString().replace('T', ' ').split('.')[0] : 'undefined';

  const fromString = format(range.from);
  const toString = range.to ? format(range.to) : 'undefined';

  return `[${fromString}, ${toString})`;
};

export const useGetCoverageFunction = (): {
  getCoverage: (
    sourceIdentifierId: number,
    dateRange: DateRange,
  ) => Promise<CoverageImagePost200Response>;
} => {
  const coverageApi = useCoverageApi();
  const queryClient = useQueryClient();

  const getCoverage = async (
    sourceIdentifierId: number,
    dateRange: DateRange,
  ): Promise<CoverageImagePost200Response> => {
    const stringDateRange = formatDateRange(dateRange);

    const cachedData = queryClient.getQueryData<CoverageImagePost200Response>([
      'coverage',
      sourceIdentifierId,
      stringDateRange,
    ]);

    if (cachedData) {
      return cachedData;
    } else {
      const data = await coverageApi.coverageImagePost({
        coverageImagePostRequest: {
          sourceIdentifierId,
          tsRange: stringDateRange,
        },
      });
      queryClient.setQueryData(
        ['coverage', sourceIdentifierId, stringDateRange],
        data,
      );
      return data;
    }
  };

  return { getCoverage };
};
